package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.core.uuidV7
import com.dotdrives.model.FormConfig
import com.dotdrives.model.GivingForm
import com.dotdrives.model.GivingFormType
import com.dotdrives.model.NewGivingForm
import com.dotdrives.model.Target
import com.dotdrives.repository.FormRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

data class NewFormUiState(
    val type: String = GivingFormType.CAMPAIGN,
    val targetId: String? = null,
    val copyFromFormId: String? = null,
    val saving: Boolean = false,
    val savedForm: GivingForm? = null,
    val error: String? = null,
)

class NewFormController(
    private val repo: FormRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val randomSuffix: () -> String = { uuidV7() },
) : DotController<NewFormUiState>(NewFormUiState(), scope) {
    fun start() = update { NewFormUiState() }

    fun setType(type: String) = update { it.copy(type = type, error = null) }

    fun setTargetId(id: String?) = update { it.copy(targetId = id, error = null) }

    fun setCopyFromFormId(id: String?) = update { it.copy(copyFromFormId = id, error = null) }

    fun create(targets: List<Target>, existingForms: List<GivingForm>) {
        val current = state.value
        if (current.saving) return
        val targetName = targets.firstOrNull { it.id == current.targetId }?.name
        val sourceConfig = current.copyFromFormId
            ?.let { id -> existingForms.firstOrNull { form -> form.id == id } }
            ?.config
        val name = defaultFormName(current.type, targetName)
        val slug = slugifyFormName(name) + "-" + randomSuffix().takeLast(SLUG_SUFFIX_LENGTH)
        update { it.copy(saving = true, error = null, savedForm = null) }
        scope.launch {
            repo.createForm(
                orgId,
                NewGivingForm(
                    name = name,
                    headline = "",
                    type = current.type,
                    targetId = current.targetId,
                    publicSlug = slug,
                    config = sourceConfig ?: FormConfig(),
                ),
            )
                .onSuccess { created -> update { it.copy(saving = false, savedForm = created, error = null) } }
                .onFailure { error -> update { it.copy(saving = false, error = error.message ?: "Something went wrong") } }
        }
    }
}

internal fun defaultFormName(type: String, targetName: String?): String {
    val typeLabel = when (type) {
        GivingFormType.CAMPAIGN -> "Campaign"
        GivingFormType.EVERGREEN -> "Evergreen"
        GivingFormType.EVENT -> "Event"
        else -> type.replaceFirstChar(Char::uppercase)
    }
    return if (targetName != null) "$targetName — $typeLabel" else "New $typeLabel form"
}

internal fun slugifyFormName(name: String): String =
    name.lowercase()
        .map { char -> if (char.isLetterOrDigit()) char else '-' }
        .joinToString("")
        .split('-')
        .filter(String::isNotEmpty)
        .joinToString("-")
        .ifEmpty { "form" }

private const val SLUG_SUFFIX_LENGTH = 6
